/**
 * Shared types for the SDX SPA navigation engine.
 *
 * Every module under navigate/ imports its types from here so the
 * vocabulary stays consistent across state stores, DOM widgets,
 * response processors, and the orchestration controller.
 */

import type {
  SdxComponentResolver,
  SdxNavigateOptions,
  SdxNavigationApi as CoreSdxNavigationApi,
  SdxNavigationState,
  SdxNavigationStatus,
} from '@sdx/core/contracts';

/**
 * Navigation scope.
 *
 * - 'content' means same pathname, different query string (pager,
 *   sort, filter). The server re-renders only the content region and
 *   can honor DataProvider tag checksums for partial reloads.
 * - 'full' means everything else: new route, new path, or the
 *   content-scope request was upgraded because the route changed.
 */
export type NavScope = 'content' | 'full';

/**
 * Navigation state machine.
 *
 * - idle: no fetch in flight; pageData reflects the current page.
 * - loading: a navigation fetch is in flight for `url`.
 * - error: the most recent navigation failed; `error` carries the
 *   human-readable reason.
 */
export type NavStatus = SdxNavigationStatus;

/**
 * The router's state: the core contract narrowed to its scope and page
 * data types.
 */
export interface NavState extends SdxNavigationState {
  status: NavStatus;
  scope: NavScope;
  pageData: PageData | null;
}

export interface NavigateOptions extends SdxNavigateOptions {
  /** Force a specific scope, bypassing the classifier. */
  scope?: NavScope;
}

/**
 * Opaque page data payload.
 *
 * Framework mount code and consumers of the navigate-end event receive
 * this verbatim. The shape varies between DataProvider responses and
 * plain SDX engine responses, which is why properties are typed loose
 * here. The fields that the controller itself reads (scope, id) are
 * narrowed on read.
 */
export interface PageData {
  scope?: NavScope;
  id?: number;
  title?: string;
  [key: string]: unknown;
}

/**
 * Partial JSON response envelope from SpaNavigationSubscriber.
 *
 * The server returns this shape for every partial SPA navigation.
 * Fields are optional because the envelope shrinks when region cache
 * tag checksums match the client copy.
 */
export interface PartialResponseEnvelope {
  pageData?: Record<string, unknown>;
  scope?: NavScope;
  route?: string;
  menuHash?: string;
  activeTheme?: string | null;
  drupalSettings?: Record<string, unknown>;
  styles?: string[];
  scripts?: (string | { src: string; type?: string })[];
  shell?: Record<string, unknown>;
  /** DataProvider response: component name + typed props. */
  component?: string;
  props?: unknown;
  _manifest?: Record<string, unknown>;
  _dependencyMap?: Record<string, string[]>;
  _tagChecksums?: Record<string, string>;
  _deferred?: unknown[];
  _lazy?: unknown[];
  layout?: unknown;
  csrfToken?: string;
  contextualLinks?: unknown[];
  /** ConfirmForm short-circuit. */
  confirm?: boolean;
  title?: string;
}

/**
 * Normalized fetch result. HTML-swap pages return type='html'; SDX
 * engine pages and DataProvider pages return type='partial'.
 */
export type FetchResult =
  | { type: 'html'; data: string; finalUrl?: string }
  | { type: 'partial'; data: PartialResponseEnvelope; finalUrl?: string }
  | { type: 'batch'; batchUrl: string; returnUrl: string };

/**
 * Parsed HTML page with the bits the content swapper needs.
 */
export interface ParsedPage {
  doc: Document;
  content: Element | null;
  title: string | null;
  settings: Record<string, unknown> | null;
  newStyles: string[];
  newScripts: import('./asset-loader').ScriptRef[];
  componentIds: string[];
}

/**
 * Component URL resolver callback.
 *
 * Framework modules register a resolver to translate SDX
 * component IDs (like "mytheme:hero-banner") into bundle URLs the
 * navigation engine can dynamic-import before the DOM swap.
 */
export type ComponentResolver = SdxComponentResolver;

/**
 * Public API installed on Drupal.sdxNavigation.
 *
 * Consumed by two audiences:
 *
 * - Framework hooks that subscribe to navigation transitions via
 *   useSyncExternalStore (React), watchEffect (Vue), or the store
 *   contract (Svelte).
 * - Framework mount scripts that register a component URL
 *   resolver so the engine can preload component bundles without
 *   knowing anything about the framework itself.
 */
export type SdxNavigationApi = CoreSdxNavigationApi<NavState>;

/**
 * Internal event names dispatched on `document` alongside the
 * subscribe API, kept for backward compatibility with consumers that
 * listen on the DOM event bus.
 */
export const NAV_EVENT = {
  START: 'sdx:navigate-start',
  END: 'sdx:navigate-end',
  ERROR: 'sdx:navigate-error',
  CONFIRM_MODAL: 'sdx:confirm-modal',
} as const;
